import { yupResolver } from '@hookform/resolvers/yup'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { AppSection } from '../../../../common/models'
import FormGroup from '../../../../core/components/FormGroup'
import {
  getSettingsAppVersion,
  updateSettingsAppVersion,
} from '../../../../core/services/festival-settings'
import { MessageBox } from '../../messages/MessageBox'
import { Button, Card, Input } from '@/components/ui'

interface IAppSectionValues {
  iosMinimumRequiredVersion: string
  androidMinimumRequiredVersion: string
}

export const AppSectionSettings: React.FC<{ hasEditPermission: boolean }> = ({
  hasEditPermission,
}) => {
  const { editionId, festivalName } = useParams()

  const [appSection, setAppSection] = useState<AppSection>()

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const getData = useCallback(async () => {
    // prettier-ignore
    (async () => {
      try {
        const resp = await getSettingsAppVersion()
        setAppSection(resp.data)
        setValue('iosMinimumRequiredVersion', resp.data.iosMinimumRequiredVersion)
        setValue('androidMinimumRequiredVersion', resp.data.androidMinimumRequiredVersion)
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName])

  useEffect(() => {
    getData()
  }, [getData])

  const schema = yup.object().shape({
    iosMinimumRequiredVersion: yup.string().required(),
    androidMinimumRequiredVersion: yup.string().required(),
  })

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm<IAppSectionValues>({
    mode: 'onChange',
    resolver: yupResolver<IAppSectionValues>(schema),
    defaultValues: {},
  })

  const submit = async (values: IAppSectionValues) => {
    try {
      const { status } = await updateSettingsAppVersion(values)

      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const handleCancel = () => {
    if (appSection) {
      setValue(
        'iosMinimumRequiredVersion',
        appSection.iosMinimumRequiredVersion,
      )
      setValue(
        'androidMinimumRequiredVersion',
        appSection.androidMinimumRequiredVersion,
      )
    }
  }

  return (
    <>
      <Card className="bg-gradient-4 p-7.5 border border-monochrome-120">
        <h3 className="text-title-xs text-monochrome-1 mb-4">App</h3>

        <form id="settings-app-section-form" onSubmit={handleSubmit(submit)}>
          <FormGroup
            label="Minimum Required Version"
            labelClassName="text-title-xs text-monochrome-1"
          >
            <Card className="flex flex-col mt-2 gap-3 w-full p-3.5">
              <div className="flex items-center gap-2">
                <FormGroup label="iOS" wrapperClassName="flex-1">
                  <Input
                    isInvalid={!!errors?.iosMinimumRequiredVersion?.message}
                    placeholder="Enter iOS Minimum Required Version"
                    {...register('iosMinimumRequiredVersion')}
                    type="text"
                    disabled={!hasEditPermission}
                  />
                </FormGroup>
                <FormGroup label="Android" wrapperClassName="flex-1">
                  <Input
                    isInvalid={!!errors?.androidMinimumRequiredVersion?.message}
                    placeholder="Enter Android Minimum Required Version"
                    {...register('androidMinimumRequiredVersion')}
                    type="text"
                    disabled={!hasEditPermission}
                  />
                </FormGroup>
              </div>
            </Card>
          </FormGroup>
        </form>

        <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-monochrome-120 [&>*]:w-[154px]">
          <Button
            onClick={handleCancel}
            variant="secondary"
            disabled={!hasEditPermission}
          >
            Cancel
          </Button>
          <Button
            form="settings-app-section-form"
            type="submit"
            variant="primary"
            disabled={isSubmitting || !hasEditPermission}
            loading={isSubmitting}
          >
            Save
          </Button>
        </div>
      </Card>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
